import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { SessionPolicy, TenantAuthUser } from '@easyflow/shared';
import {
  decodeJwtExpMs,
  decodeJwtPayload,
  getSessionIdleStore,
  sessionDeadlineStorageKey,
  sessionExpiredStorageKey,
  SessionTimeoutBanner,
  type SessionExpireEvent,
  type SessionStartReason,
} from '@easyflow/ui-frontend';
import {
  apiClient,
  authClient,
  AUTH_TOKEN_REFRESHED_EVENT,
  AUTH_UNAUTHORIZED_EVENT,
  refreshSession,
  refreshSessionDetailed,
  type AuthTokenRefreshedDetail,
  type RefreshSessionResult,
} from '@/lib/api';
import { persistSessionPolicy, resolveSessionDurations } from '@/lib/session-policy';
import { AUTH_STORAGE_KEYS, clearAuthStorage } from '@/constants/auth';

/** Alias locale del contratto condiviso (SoT in @easyflow/shared). */
export type W3User = TenantAuthUser;

interface AuthState {
  token: string | null;
  user: W3User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface LoginInput {
  readonly token: string;
  readonly user?: W3User;
  readonly refreshToken?: string;
  readonly sessionPolicy?: SessionPolicy;
}

interface AuthContextValue extends AuthState {
  login: (input: LoginInput) => void;
  logout: () => Promise<void>;
  tenantSlug: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SIGNED_OUT: AuthState = { token: null, user: null, isAuthenticated: false, isLoading: false };

/**
 * Attese tra i tentativi di rinnovo al bootstrap quando il server non
 * risponde (rete/5xx): una breve interruzione non deve buttare una sessione
 * ancora valida. Esauriti i tentativi le credenziali restano in storage e il
 * prossimo caricamento riprova.
 */
const BOOTSTRAP_REFRESH_RETRY_DELAYS_MS: readonly number[] = [1_000, 3_000];

interface RevocableCredentials {
  readonly refreshToken: string;
  readonly tenantSlug: string;
}

function readRevocableCredentials(): RevocableCredentials | null {
  const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  const tenantSlug = localStorage.getItem(AUTH_STORAGE_KEYS.TENANT_SLUG);
  return refreshToken && tenantSlug ? { refreshToken, tenantSlug } : null;
}

/** Revoca lato server, idempotente e best-effort: non blocca mai la chiusura locale. */
function revokeOnServer(credentials: RevocableCredentials | null): Promise<void> {
  if (!credentials) return Promise.resolve();
  return authClient
    .post(`/${credentials.tenantSlug}/logout`, { refreshToken: credentials.refreshToken })
    .then(() => undefined)
    .catch(() => undefined);
}

function delay(ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Rinnovo al bootstrap con retry sui soli errori temporanei. */
async function refreshWithRetry(isCancelled: () => boolean): Promise<RefreshSessionResult> {
  let result = await refreshSessionDetailed();
  for (const wait of BOOTSTRAP_REFRESH_RETRY_DELAYS_MS) {
    if (result.kind !== 'transient' || isCancelled()) return result;
    await delay(wait);
    if (isCancelled()) return result;
    result = await refreshSessionDetailed();
  }
  return result;
}

/** Store condiviso di sessione per inattività: W3 è una sessione tenant. */
const sessionStore = getSessionIdleStore('tenant');

function readString(payload: Record<string, unknown> | null, key: string): string | undefined {
  const value = payload?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function isExpired(token: string): boolean {
  const expMs = decodeJwtExpMs(token);
  return expMs !== null && expMs <= Date.now();
}

function readStoredUser(): W3User | null {
  const userStr = localStorage.getItem(AUTH_STORAGE_KEYS.USER);
  if (!userStr) return null;
  try {
    return JSON.parse(userStr) as W3User;
  } catch {
    return null;
  }
}

/**
 * Al bootstrap: la sessione per inattività è già chiusa se la deadline
 * persistita dal motore condiviso è nel passato, oppure se non c'è deadline
 * ma un'altra tab ha lasciato il marker di scadenza. Nessuna delle due
 * (es. primo avvio dopo il deploy) = sessione ancora rinnovabile.
 */
function isPersistedSessionDeadlinePassed(now: number): boolean {
  const rawDeadline = localStorage.getItem(sessionDeadlineStorageKey('tenant'));
  if (rawDeadline !== null) {
    const deadline = Number(rawDeadline);
    return Number.isFinite(deadline) && deadline <= now;
  }
  return localStorage.getItem(sessionExpiredStorageKey('tenant')) !== null;
}

function isTokenRefreshedEvent(event: Event): event is CustomEvent<AuthTokenRefreshedDetail> {
  if (!(event instanceof CustomEvent)) return false;
  const detail: unknown = event.detail;
  return typeof detail === 'object' && detail !== null && 'token' in detail && typeof detail.token === 'string';
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    token: null,
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });
  // Evita setState dopo l'unmount nel bootstrap asincrono (refresh all'avvio).
  const mountedRef = useRef(true);

  const clearLocalSession = useCallback((): void => {
    sessionStore.stop();
    clearAuthStorage();
    apiClient.clearToken();
    if (mountedRef.current) setState(SIGNED_OUT);
  }, []);

  /**
   * Chiusura della sessione: l'accesso locale termina SUBITO (stato, storage,
   * store), la revoca server-side parte in parallelo con le credenziali
   * catturate prima della pulizia. Una richiesta di logout lenta o persa non
   * può lasciare la UI autenticata oltre la scadenza.
   */
  const terminateSession = useCallback(
    (revoke: boolean): Promise<void> => {
      const credentials = revoke ? readRevocableCredentials() : null;
      clearLocalSession();
      return revokeOnServer(credentials);
    },
    [clearLocalSession]
  );

  const handleExpire = useCallback(
    (event: SessionExpireEvent): void => {
      // Solo la tab initiator revoca lato server; le altre puliscono e basta.
      // Il redirect al login lo fa `RequireAuth` quando isAuthenticated cade.
      void terminateSession(event.isInitiator);
    },
    [terminateSession]
  );

  const startSession = useCallback(
    (reason: SessionStartReason): void => {
      sessionStore.start(
        {
          scope: 'tenant',
          ...resolveSessionDurations(),
          getAccessToken: () => localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN),
          onRefreshNeeded: refreshSession,
          onExpire: handleExpire,
        },
        { reason }
      );
    },
    [handleExpire]
  );

  // Bootstrap: token valido → sessione ripresa; access scaduto ma refresh
  // presente e deadline di inattività non passata → rinnovo silenzioso.
  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    /** True solo se lo store è davvero in corso dopo il restore (deadline non scaduta). */
    const adopt = (token: string): boolean => {
      apiClient.setToken(token);
      startSession('restore');
      if (sessionStore.getStatus() !== 'running') return false;
      setState({ token, user: readStoredUser(), isAuthenticated: true, isLoading: false });
      return true;
    };

    const bootstrap = async (): Promise<void> => {
      const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
      const idleExpired = isPersistedSessionDeadlinePassed(Date.now());
      // Access ancora valido ma inattività scaduta (o marker di un'altra tab):
      // la sessione è chiusa, il token non riapre nulla.
      if (token && !isExpired(token) && !idleExpired && adopt(token)) return;
      const hasRefresh = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN) !== null;
      if (!idleExpired && hasRefresh && (!token || isExpired(token))) {
        const result = await refreshWithRetry(() => cancelled);
        if (cancelled) return;
        if (result.kind === 'ok' && adopt(result.token)) return;
        if (result.kind === 'transient') {
          // Server irraggiungibile: sessione ancora valida, credenziali e
          // deadline conservate per il prossimo caricamento; ora si va al login.
          setState(SIGNED_OUT);
          return;
        }
      }
      // Inattività scaduta a tab chiusa: nessuno ha revocato lato server, lo fa
      // questo caricamento (best-effort), poi via tutto.
      if (idleExpired) void revokeOnServer(readRevocableCredentials());
      // Nessuna sessione rinnovabile: storage ripulito (token scaduti, policy orfana).
      clearAuthStorage();
      apiClient.clearToken();
      setState(SIGNED_OUT);
    };

    void bootstrap();
    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, [startSession]);

  useEffect(() => {
    const onUnauthorized = (): void => {
      clearLocalSession();
    };
    const onTokenRefreshed = (event: Event): void => {
      if (!isTokenRefreshedEvent(event)) return;
      const { token, sessionPolicy } = event.detail;
      // Policy aggiornata a caldo (una modifica dal master arriva al primo refresh).
      if (sessionPolicy && sessionStore.getStatus() === 'running') {
        sessionStore.updateConfig(resolveSessionDurations());
      }
      setState((prev) => (prev.isAuthenticated ? { ...prev, token } : prev));
    };
    // Logout esplicito (o sessione chiusa) in un'altra tab: il motore
    // condiviso non propaga la rimozione della deadline, lo fa l'adapter.
    const onStorage = (event: StorageEvent): void => {
      if (event.key === AUTH_STORAGE_KEYS.TOKEN && event.newValue === null) clearLocalSession();
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    window.addEventListener(AUTH_TOKEN_REFRESHED_EVENT, onTokenRefreshed);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
      window.removeEventListener(AUTH_TOKEN_REFRESHED_EVENT, onTokenRefreshed);
      window.removeEventListener('storage', onStorage);
    };
  }, [clearLocalSession]);

  const login = useCallback(
    ({ token, user, refreshToken, sessionPolicy }: LoginInput): void => {
      localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token);
      if (refreshToken) localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      if (sessionPolicy) persistSessionPolicy(sessionPolicy);
      const payload = decodeJwtPayload(token);
      const mergedUser: W3User = {
        id: user?.id ?? readString(payload, 'sub') ?? '',
        email: user?.email ?? readString(payload, 'email') ?? '',
        ...user,
      };
      localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(mergedUser));
      const slug = mergedUser.tenantSlug ?? readString(payload, 'tenantSlug') ?? readString(payload, 'tenant_slug');
      if (slug) localStorage.setItem(AUTH_STORAGE_KEYS.TENANT_SLUG, slug);
      apiClient.setToken(token);
      // Il login è attività: nuova deadline, marker precedenti scartati.
      startSession('login');
      setState({ token, user: mergedUser, isAuthenticated: true, isLoading: false });
    },
    [startSession]
  );

  const logout = useCallback((): Promise<void> => terminateSession(true), [terminateSession]);

  const tenantSlug = useMemo(() => {
    if (state.user?.tenantSlug) return state.user.tenantSlug;
    if (state.token) {
      const payload = decodeJwtPayload(state.token);
      return readString(payload, 'tenantSlug') ?? readString(payload, 'tenant_slug') ?? null;
    }
    return localStorage.getItem(AUTH_STORAGE_KEYS.TENANT_SLUG);
  }, [state.user, state.token]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, tenantSlug }),
    [state, login, logout, tenantSlug]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {state.isAuthenticated && (
        <SessionTimeoutBanner scope="tenant" onKeepAlive={refreshSession} onLogout={logout} />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve essere usato dentro AuthProvider');
  return ctx;
}
